import React, { useState, useEffect, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { useLanguage } from '../contexts/LanguageContext';
import './BugReportModal.css';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (description: string, screenshot?: Blob) => Promise<void>;
  initialScreenshot?: { blob: Blob; preview: string } | null;
}

const BugReportModal: React.FC<BugReportModalProps> = ({ isOpen, onClose, onSubmit, initialScreenshot }) => {
  const { t } = useLanguage();
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [screenshot, setScreenshot] = useState<Blob | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [isHiddenForCapture, setIsHiddenForCapture] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  // Initialize with the pre-captured screenshot when modal opens
  useEffect(() => {
    if (isOpen && initialScreenshot) {
      setScreenshot(initialScreenshot.blob);
      setScreenshotPreview(initialScreenshot.preview);
    }
  }, [isOpen, initialScreenshot]);

  // Hide modal and show floating capture button
  const handleStartRecapture = useCallback(() => {
    setIsHiddenForCapture(true);
  }, []);

  // Capture screen when user clicks the floating button
  const handleCaptureNow = useCallback(async () => {
    setIsCapturing(true);

    // Small delay to ensure button click animation is not captured
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      const canvas = await html2canvas(document.body, {
        allowTaint: true,
        useCORS: true,
        logging: false,
        ignoreElements: (element) => {
          // Ignore the floating capture button itself
          return element.classList.contains('floating-capture-button');
        }
      });

      canvas.toBlob((blob) => {
        if (blob) {
          setScreenshot(blob);
          setScreenshotPreview(canvas.toDataURL());
        }
        setIsHiddenForCapture(false);
        setIsCapturing(false);
      }, 'image/png');
    } catch (err) {
      console.error('Error capturing screenshot:', err);
      setError(t('bugReport.errors.captureError'));
      setIsHiddenForCapture(false);
      setIsCapturing(false);
    }
  }, [t]);

  // Cancel recapture and go back to modal
  const handleCancelRecapture = useCallback(() => {
    setIsHiddenForCapture(false);
  }, []);

  const handleRemoveScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!description.trim()) {
      setError(t('bugReport.errors.descriptionRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(description, screenshot || undefined);
      setSuccess(true);
      setTimeout(() => {
        setDescription('');
        setScreenshot(null);
        setScreenshotPreview(null);
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || t('bugReport.errors.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setDescription('');
      setError('');
      setSuccess(false);
      setScreenshot(null);
      setScreenshotPreview(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  // Show floating capture button when user wants to recapture
  if (isHiddenForCapture) {
    return (
      <div className="floating-capture-button" onClick={handleCaptureNow}>
        <div className="capture-button-content">
          <span className="capture-icon">{isCapturing ? '⏳' : '📸'}</span>
          <span className="capture-text">
            {isCapturing ? t('bugReport.capturing') : t('bugReport.clickToCapture')}
          </span>
        </div>
        <button
          className="capture-cancel-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleCancelRecapture();
          }}
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div className="bug-report-overlay" onClick={handleClose}>
      <div className="bug-report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bug-report-header">
          <h2>🐛 {t('bugReport.title')}</h2>
          <button className="bug-report-close" onClick={handleClose} disabled={isSubmitting}>
            ✕
          </button>
        </div>

        {success ? (
          <div className="bug-report-success">
            <div className="success-icon">✓</div>
            <p>{t('bugReport.successMessage')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bug-report-form">
            <div className="bug-report-info">
              <p>{t('bugReport.infoMessage')}</p>
            </div>

            <div className="form-group">
              <label htmlFor="description">{t('bugReport.descriptionLabel')}</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('bugReport.descriptionPlaceholder')}
                rows={6}
                maxLength={1000}
                disabled={isSubmitting}
                required
              />
              <div className="char-count">
                {description.length}/1000
              </div>
            </div>

            <div className="form-group">
              <label>{t('bugReport.screenshotLabel')}</label>
              {screenshotPreview ? (
                <div className="screenshot-preview">
                  <img src={screenshotPreview} alt="Screenshot preview" />
                  <div className="screenshot-actions">
                    <button
                      type="button"
                      onClick={handleStartRecapture}
                      className="btn-recapture"
                      disabled={isSubmitting}
                    >
                      📷 {t('bugReport.recaptureScreen')}
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveScreenshot}
                      className="btn-remove-screenshot"
                      disabled={isSubmitting}
                    >
                      ✕ {t('bugReport.removeScreenshot')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="no-screenshot">
                  <p className="no-screenshot-text">{t('bugReport.noScreenshot')}</p>
                  <button
                    type="button"
                    onClick={handleStartRecapture}
                    className="btn-capture"
                    disabled={isSubmitting}
                  >
                    📸 {t('bugReport.captureScreen')}
                  </button>
                </div>
              )}
            </div>

            {error && <div className="bug-report-error">{error}</div>}

            <div className="bug-report-actions">
              <button
                type="button"
                onClick={handleClose}
                className="btn-cancel"
                disabled={isSubmitting}
              >
                {t('bugReport.cancel')}
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('bugReport.submitting') : t('bugReport.submit')}
              </button>
            </div>

            <div className="bug-report-privacy">
              <small>
                {t('bugReport.privacyNote')}
              </small>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BugReportModal;
